import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useUserSettingsStore } from '@/stores/useUserSettingsStore'
import { useDevModeStore } from '@/stores/useDevModeStore'
import { createFileRoute, useRouteContext } from '@tanstack/react-router'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

export const Route = createFileRoute(
  '/_authenticated/_settings/user/$userId/advanced-settings',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { devMode, setDevMode } = useDevModeStore()
  const hideDeleteConfirmationDialog = useUserSettingsStore(
    (state) => state.hideDeleteConfirmationDialog,
  )
  const setHideDeleteConfirmationDialog = useUserSettingsStore(
    (state) => state.setHideDeleteConfirmationDialog,
  )
  const regenerateDocumentTitle = useUserSettingsStore(
    (state) => state.regenerateDocumentTitle,
  )
  const setRegenerateDocumentTitle = useUserSettingsStore(
    (state) => state.setRegenerateDocumentTitle,
  )
  const defaultProcessingResolution = useUserSettingsStore(
    (state) => state.defaultProcessingResolution,
  )
  const setDefaultProcessingResolution = useUserSettingsStore(
    (state) => state.setDefaultProcessingResolution,
  )
  const exportOrientation = useUserSettingsStore(
    (state) => state.exportOrientation,
  )
  const setExportOrientation = useUserSettingsStore(
    (state) => state.setExportOrientation,
  )

  const { isSysAdmin } = useRouteContext({
    from: '/_authenticated',
  })

  return (
    <div className="settings-container">
      <h1 className="text-2xl">Advanced Settings</h1>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg">Notifications & Alerts</h2>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-bold">Delete Confirmation Dialog</h3>
          <div className="bg-secondary flex flex-row gap-2 rounded-lg px-3 py-4">
            <div className="flex items-center gap-2">
              <Switch
                id="hide-dialog"
                checked={hideDeleteConfirmationDialog}
                onCheckedChange={() =>
                  setHideDeleteConfirmationDialog(!hideDeleteConfirmationDialog)
                }
              />
              <span className="text-copy-secondary min-w-10 text-sm font-medium">
                {hideDeleteConfirmationDialog ? 'Hide' : 'Show'}
              </span>
            </div>
            <Label htmlFor="hide-dialog" className="cursor-pointer">
              Show or hide the delete confirmation prompt that appears before
              deleting a document.
            </Label>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg">Content Processing Settings</h2>
        </div>
        {/* File Naming Settings for SOP */}
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-bold">File Naming Settings</h3>
          <p className="text-copy-secondary mb-2 text-sm">
            Customize how generated documents are named by default
          </p>
          <RadioGroup
            value={regenerateDocumentTitle ? 'regenerate' : 'original'}
            onValueChange={(value) =>
              setRegenerateDocumentTitle(value === 'regenerate')
            }
            className="bg-secondary rounded-lg px-3 py-4"
          >
            <div className="flex gap-2">
              <RadioGroupItem value="original" id="original"></RadioGroupItem>
              <div className="flex flex-col gap-2">
                <Label htmlFor="original" className="cursor-pointer">
                  Use Original Filename
                </Label>
                <span className="text-copy-secondary text-xs">
                  Keep the original filename for the uploaded content (i.e.
                  packing video.mp4).
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <RadioGroupItem
                value="regenerate"
                id="regenerate"
              ></RadioGroupItem>
              <div className="flex flex-col gap-2">
                <Label htmlFor="regenerate" className="cursor-pointer">
                  AI-Generated Filename
                </Label>
                <span className="text-copy-secondary text-xs">
                  Automatically generate a file name based on the uploaded
                  content (i.e. SOP Box Assembly Tutorial).
                </span>
              </div>
            </div>
          </RadioGroup>
        </div>
        {/* Processing Resolution Settings */}
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-bold">Default Processing Resolution</h3>
          <p className="text-copy-secondary mb-2 text-sm">
            Choose the level of detail used during processing
          </p>
          <RadioGroup
            value={defaultProcessingResolution}
            onValueChange={(value) =>
              setDefaultProcessingResolution(value as 'SD' | 'Original')
            }
            className="bg-secondary rounded-lg px-3 py-4"
          >
            <div className="flex gap-2">
              <RadioGroupItem value="SD" id="sd"></RadioGroupItem>
              <div className="flex flex-col gap-2">
                <Label htmlFor="sd" className="cursor-pointer">
                  Regular (Fast)
                </Label>
                <span className="text-copy-secondary text-xs">
                  Optimized for speed; suitable for standard SOP extracted
                  content.
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <RadioGroupItem
                value="Original"
                id="original-res"
              ></RadioGroupItem>
              <div className="flex flex-col gap-2">
                <Label htmlFor="original-res" className="cursor-pointer">
                  High (Detailed)
                </Label>
                <span className="text-copy-secondary text-xs">
                  Produces more detailed and accurate SOP steps; processing may
                  take longer.
                </span>
              </div>
            </div>
          </RadioGroup>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg">Export Settings</h2>
        </div>
        {/* Export Orientation Settings */}
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-bold">Export Orientation</h3>
          <p className="text-copy-secondary mb-2 text-sm">
            Manage how document exports are generated.
          </p>
          <RadioGroup
            value={exportOrientation}
            onValueChange={(value) =>
              setExportOrientation(value as 'Auto' | 'Portrait' | 'Landscape')
            }
            className="bg-secondary rounded-lg px-3 py-4"
          >
            <div className="flex gap-2">
              <RadioGroupItem value="Auto" id="auto"></RadioGroupItem>
              <div className="flex flex-col gap-2">
                <Label htmlFor="auto" className="cursor-pointer">
                  Auto
                </Label>
                <span className="text-copy-secondary text-xs">
                  Automatically determines orientation based on your
                  organization&apos;s PDF template setting.
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <RadioGroupItem value="Portrait" id="portrait"></RadioGroupItem>
              <div className="flex flex-col gap-2">
                <Label htmlFor="portrait" className="cursor-pointer">
                  Portrait
                </Label>
                <span className="text-copy-secondary text-xs">
                  Ideal for mobile viewing or documents that follow a vertical
                  reading flow. Keeps the export in a traditional page layout.
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <RadioGroupItem value="Landscape" id="landscape"></RadioGroupItem>
              <div className="flex flex-col gap-2">
                <Label htmlFor="landscape" className="cursor-pointer">
                  Landscape
                </Label>
                <span className="text-copy-secondary text-xs">
                  Provides a wider layout that fits more elements per row and
                  improves readability for step-by-step SOPs.
                </span>
              </div>
            </div>
          </RadioGroup>
        </div>
      </div>
      {isSysAdmin ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg">Developer Settings</h2>
            <p className="text-copy-secondary text-sm">
              Visible to only System Admins (LineWise Team)
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-bold">Dev Mode</h3>
            <div className="bg-secondary flex flex-row items-center gap-2 rounded-lg px-3 py-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="dev-mode-toggle"
                  checked={devMode}
                  onCheckedChange={() => setDevMode(!devMode)}
                />
                <span className="text-copy-secondary min-w-10 text-sm font-medium">
                  {devMode ? 'On' : 'Off'}
                </span>
              </div>
              <Label htmlFor="dev-mode-toggle" className="cursor-pointer">
                Enables internal debugging tools and advanced features. Visible
                only to LineWise admins.
              </Label>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
